import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import DefileCarousel from '../components/DefileCarousel';
import BrandLogo from '../components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGES = [
  '/images/hero-1.jpeg',
  '/images/hero-2.jpeg',
  '/images/hero-3.jpeg',
  '/images/hero-4.jpeg',
];
const HERO_ROTATION_MS = 5 * 60 * 1000; // 5 minutes

const HISTOIRE_PARAGRAPHS = [
  "MAISON KA est née d'une conviction : nos héritages méritent d'être portés, transmis et célébrés.",
  "Inspirée par les savoir-faire amazighs, les récits de transmission et la richesse des patrimoines méditerranéens, MAISON KA crée des pièces contemporaines qui dialoguent avec la mémoire.",
  "Chaque création est imaginée comme une rencontre entre tradition et modernité. Les lignes sont pensées avec exigence, les matières sélectionnées avec soin et les détails travaillés pour révéler l'élégance d'un artisanat vivant.",
  "Plus qu'une maison de création, MAISON KA est un espace d'expression culturelle où le vêtement devient un langage, une histoire à porter et un lien entre les générations.",
];

const PIECES_PARAGRAPHS = [
  "Chez MAISON KA, chaque pièce est imaginée comme une œuvre textile alliant raffinement, confort et identité.",
  "Nos collections puisent leur inspiration dans les formes, les symboles et les savoir-faire traditionnels amazighs, réinterprétés dans une esthétique contemporaine. Les coupes sont pensées pour sublimer chaque silhouette tandis que les finitions témoignent d'une attention particulière portée au détail.",
  "Nos créations sont réalisées en séries limitées ou sur commande afin de préserver leur caractère unique et leur qualité d'exécution. Porter MAISON KA, c'est choisir une pièce qui raconte une histoire et affirme une élégance authentique.",
];

const EVENEMENTS_PARAGRAPHS = [
  "MAISON KA imagine et participe à des événements qui mettent à l'honneur l'artisanat, la transmission et les expressions culturelles contemporaines.",
  "Défilés, expositions, rencontres artistiques, conférences ou collaborations institutionnelles : chaque événement est conçu comme une expérience immersive permettant de découvrir l'univers de la Maison au-delà du vêtement.",
  "Ces moments privilégiés favorisent les échanges, créent des ponts entre les cultures et valorisent les talents qui font vivre notre patrimoine.",
];

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [defile, setDefile] = useState({ images: [], title: 'Nos événements', subtitle: '' });
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, HERO_ROTATION_MS);
    return () => clearInterval(interval);
  }, []);

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) {
      toast.error('Veuillez saisir votre email.');
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source: 'home' });
      toast.success('Merci ! Votre inscription est bien enregistrée.');
      setNewsletterEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'inscription a échoué, réessayez.");
    } finally {
      setSubscribing(false);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});

        const [featuredRes, newRes] = await Promise.all([
          axios.get(`${API}/products?is_featured=true`),
          axios.get(`${API}/products?is_new=true`),
        ]);

        setFeaturedProducts(featuredRes.data.slice(0, 4));
        setNewProducts(newRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();

    axios
      .get(`${API}/settings`)
      .then((res) => {
        const d = res.data?.defile;
        if (d && Array.isArray(d.images)) setDefile(d);
      })
      .catch((error) => console.error('Error fetching defile settings:', error));
  }, []);

  return (
    <div className="grain-overlay">
      {/* Hero */}
      <section className="relative h-screen flex items-end pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <AnimatePresence>
            <motion.img
              key={heroIndex}
              src={HERO_IMAGES[heroIndex]}
              alt="MAISON KA — créations contemporaines"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="max-w-2xl"
          >
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-4">Maison de création</p>
            <div className="mb-6">
              <BrandLogo size="hero" tone="onDark" />
            </div>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-4 max-w-lg">
              {HISTOIRE_PARAGRAPHS[0]}
            </p>
            <p className="text-white/50 text-sm md:text-base italic mb-8">
              {"L'élégance d'un héritage en mouvement."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/shop')}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-shop-btn"
              >
                Découvrir nos pièces
                <ArrowRight size={16} />
              </button>
              <button
                onClick={() => document.getElementById('notre-histoire')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
                data-testid="hero-story-btn"
              >
                Notre histoire
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="text-gold animate-bounce" size={32} />
        </motion.div>
      </section>

      {/* Notre histoire */}
      <section id="notre-histoire" className="relative py-24 md:py-32 overflow-hidden" data-testid="histoire-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1752321532730-ed24a51e7b3f?w=1920&q=80"
            alt="Savoir-faire artisanal"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold text-xs uppercase tracking-widest"
          >
            Notre histoire
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl md:text-5xl tracking-tight mt-2 mb-10 max-w-2xl"
          >
            Un héritage à <span className="text-gold">porter</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl">
            {HISTOIRE_PARAGRAPHS.map((paragraph, index) => (
              <motion.p
                key={paragraph.slice(0, 32)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-foreground/70 leading-relaxed"
              >
                {paragraph}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* Nos événements */}
      <DefileCarousel
        images={defile.images}
        title={defile.title || 'Nos événements'}
        subtitle={defile.subtitle || 'Quand la création rencontre la culture'}
        description={EVENEMENTS_PARAGRAPHS}
        ctaLabel="Découvrez nos prochains rendez-vous"
        ctaHref="/contact"
      />

      {/* Nos pièces */}
      <section id="nos-pieces" className="py-24 md:py-32 bg-background" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-12 mb-12">
            <div className="max-w-2xl">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-gold text-xs uppercase tracking-widest"
              >
                Nos pièces
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading text-3xl md:text-5xl tracking-tight mt-2"
              >
                Des créations singulières
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-foreground/60 mt-4 leading-relaxed"
              >
                Des créations singulières, conçues pour traverser le temps.
              </motion.p>
            </div>
            <Link
              to="/shop?is_featured=true"
              className="gold-underline text-sm uppercase tracking-widest text-foreground/60 hover:text-gold transition-colors shrink-0"
              data-testid="view-all-featured"
            >
              Voir tout
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-5xl">
            {PIECES_PARAGRAPHS.map((paragraph, index) => (
              <motion.p
                key={paragraph.slice(0, 32)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="text-foreground/70 text-sm leading-relaxed"
              >
                {paragraph}
              </motion.p>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-[3/4] bg-black/5 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Dernières créations */}
      <section className="py-24 md:py-32 bg-[#EDEEDF]" data-testid="new-arrivals-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-gold text-xs uppercase tracking-widest"
              >
                Nouveautés
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading text-3xl md:text-5xl tracking-tight mt-2"
              >
                Dernières Créations
              </motion.h2>
            </div>
            <Link
              to="/shop?is_new=true"
              className="gold-underline text-sm uppercase tracking-widest text-foreground/60 hover:text-gold transition-colors"
              data-testid="view-all-new"
            >
              Voir tout
            </Link>
          </div>

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {newProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-3xl md:text-5xl tracking-tight mb-6"
          >
            Rejoignez l'<span className="text-gold">aventure</span> MAISON KA
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-foreground/60 max-w-xl mx-auto mb-8"
          >
            Inscrivez-vous pour être informé de nos nouvelles collections, de nos événements
            et bénéficier d'offres exclusives.
          </motion.p>
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            onSubmit={subscribeNewsletter}
            className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
          >
            <input
              type="email"
              placeholder="Votre email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1 bg-transparent border-b border-black/20 py-3 px-0 focus:border-gold focus:outline-none text-foreground placeholder:text-foreground/40"
              data-testid="newsletter-email"
            />
            <button type="submit" disabled={subscribing} className="btn-primary whitespace-nowrap" data-testid="newsletter-submit">
              {subscribing ? 'Inscription…' : "S'inscrire"}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
